import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { emailApi } from '../../lib/api';
import { useAuth } from '../../context/AuthContext';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { toast } from 'sonner';
import { ArrowLeft, Mail, Inbox, Send, Trash2, Plus, Star, MailOpen, Reply, ChevronLeft } from 'lucide-react';

const TABS = [
  { id: 'inbox', label: 'Inbox', icon: Inbox },
  { id: 'sent', label: 'Sent', icon: Send },
  { id: 'trash', label: 'Trash', icon: Trash2 },
];

export default function EmailPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [tab, setTab] = useState('inbox');
  const [emails, setEmails] = useState([]);
  const [loading, setLoading] = useState(true);
  const [unreadCount, setUnreadCount] = useState(0);
  const [showCompose, setShowCompose] = useState(false);
  const [selectedEmail, setSelectedEmail] = useState(null);
  const [compose, setCompose] = useState({ to: '', subject: '', body: '', reply_to: null });

  useEffect(() => { loadEmails(); }, [tab]);

  const loadEmails = async () => {
    setLoading(true);
    try {
      let data;
      if (tab === 'inbox') { data = await emailApi.getInbox(); setUnreadCount(data.unread_count || 0); }
      else if (tab === 'sent') { data = await emailApi.getSent(); }
      else { data = await emailApi.getTrash(); }
      setEmails(data.emails || []);
    } catch { toast.error('Failed to load emails'); }
    finally { setLoading(false); }
  };

  const handleCompose = async () => {
    if (!compose.to.trim() || !compose.body.trim()) { toast.error('Recipient and body required'); return; }
    try {
      await emailApi.compose(compose);
      toast.success('Email sent!');
      setShowCompose(false);
      setCompose({ to: '', subject: '', body: '', reply_to: null });
      if (tab === 'sent') loadEmails();
    } catch (e) { toast.error(e.message); }
  };

  const openEmail = async (email) => {
    try {
      const full = await emailApi.get(email.email_id);
      setSelectedEmail(full);
      if (tab === 'inbox' && !email.is_read) {
        setEmails(prev => prev.map(e => e.email_id === email.email_id ? { ...e, is_read: true } : e));
        setUnreadCount(prev => Math.max(0, prev - 1));
      }
    } catch { toast.error('Failed to load email'); }
  };

  const handleTrash = async (emailId, e) => {
    if (e) { e.stopPropagation(); }
    try {
      if (tab === 'trash') { await emailApi.delete(emailId); }
      else { await emailApi.trash(emailId); }
      setEmails(prev => prev.filter(em => em.email_id !== emailId));
      if (selectedEmail?.email_id === emailId) setSelectedEmail(null);
      toast.success(tab === 'trash' ? 'Deleted permanently' : 'Moved to trash');
    } catch (err) { toast.error(err.message); }
  };

  const handleReply = (email) => {
    setCompose({ to: email.from_email, subject: `Re: ${email.subject}`, body: '', reply_to: email.email_id });
    setShowCompose(true);
    setSelectedEmail(null);
  };

  const formatDate = (ts) => {
    const d = new Date(ts);
    const now = new Date();
    if (d.toDateString() === now.toDateString()) return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    return d.toLocaleDateString([], { month: 'short', day: 'numeric' });
  };

  return (
    <div className="min-h-screen bg-background flex flex-col" data-testid="email-page">
      <header className="sticky top-0 z-30 border-b border-border bg-background/95 backdrop-blur">
        <div className="max-w-6xl mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="icon" onClick={() => navigate('/suite')} data-testid="back-btn"><ArrowLeft className="w-5 h-5" /></Button>
            <Mail className="w-6 h-6 text-red-500" />
            <h1 className="text-xl font-bold text-foreground">Email</h1>
          </div>
          <Button onClick={() => { setCompose({ to: '', subject: '', body: '', reply_to: null }); setShowCompose(true); }} data-testid="compose-btn">
            <Plus className="w-4 h-4 mr-1" /> Compose
          </Button>
        </div>
      </header>

      <div className="flex-1 flex max-w-6xl mx-auto w-full">
        {/* Sidebar */}
        <div className="w-52 border-r border-border p-3 space-y-1 shrink-0">
          {TABS.map(t => (
            <button key={t.id} onClick={() => { setTab(t.id); setSelectedEmail(null); }}
              className={`w-full flex items-center gap-2.5 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${tab === t.id ? 'bg-primary/10 text-primary' : 'text-muted-foreground hover:bg-muted hover:text-foreground'}`}
              data-testid={`tab-${t.id}`}>
              <t.icon className="w-4 h-4" />
              {t.label}
              {t.id === 'inbox' && unreadCount > 0 && (
                <span className="ml-auto text-[10px] bg-primary text-primary-foreground rounded-full px-1.5 py-0.5 font-bold">{unreadCount}</span>
              )}
            </button>
          ))}
        </div>

        {/* Email List / Detail */}
        <div className="flex-1 flex">
          {selectedEmail ? (
            <div className="flex-1 p-6">
              <Button variant="ghost" size="sm" onClick={() => setSelectedEmail(null)} className="mb-4"><ChevronLeft className="w-4 h-4 mr-1" /> Back</Button>
              <div className="max-w-3xl">
                <h2 className="text-xl font-bold text-foreground mb-2">{selectedEmail.subject || '(No Subject)'}</h2>
                <div className="flex items-center gap-3 mb-4 text-sm text-muted-foreground">
                  <span className="font-medium text-foreground">{selectedEmail.from_name}</span>
                  <span>&lt;{selectedEmail.from_email}&gt;</span>
                  <span className="ml-auto">{new Date(selectedEmail.created_at).toLocaleString()}</span>
                </div>
                <div className="prose prose-sm dark:prose-invert max-w-none p-4 rounded-lg bg-card border border-border whitespace-pre-wrap">{selectedEmail.body}</div>
                {selectedEmail.thread?.length > 0 && (
                  <div className="mt-4 space-y-3">
                    <h3 className="text-sm font-semibold text-muted-foreground">Thread</h3>
                    {selectedEmail.thread.map(t => (
                      <div key={t.email_id} className="p-3 rounded-lg bg-muted/50 border border-border">
                        <div className="flex items-center gap-2 text-xs text-muted-foreground mb-1">
                          <span className="font-medium text-foreground">{t.from_name}</span>
                          <span>{formatDate(t.created_at)}</span>
                        </div>
                        <p className="text-sm text-foreground whitespace-pre-wrap">{t.body}</p>
                      </div>
                    ))}
                  </div>
                )}
                <div className="mt-4 flex gap-2">
                  <Button variant="outline" onClick={() => handleReply(selectedEmail)} data-testid="reply-btn"><Reply className="w-4 h-4 mr-1" /> Reply</Button>
                  <Button variant="outline" onClick={() => handleTrash(selectedEmail.email_id)}><Trash2 className="w-4 h-4 mr-1" /> {tab === 'trash' ? 'Delete' : 'Trash'}</Button>
                </div>
              </div>
            </div>
          ) : (
            <div className="flex-1 overflow-y-auto">
              {loading ? (
                <div className="p-4 space-y-2">{[1,2,3,4].map(i => <div key={i} className="h-16 bg-muted animate-pulse rounded" />)}</div>
              ) : emails.length === 0 ? (
                <div className="flex items-center justify-center h-full">
                  <div className="text-center">
                    <Mail className="w-16 h-16 mx-auto text-muted-foreground/20 mb-3" />
                    <p className="text-lg font-semibold text-foreground">No emails</p>
                    <p className="text-sm text-muted-foreground">{tab === 'inbox' ? 'Your inbox is empty' : tab === 'sent' ? 'No sent emails' : 'Trash is empty'}</p>
                  </div>
                </div>
              ) : (
                emails.map(email => (
                  <div key={email.email_id} onClick={() => openEmail(email)}
                    className={`flex items-center gap-3 px-4 py-3 border-b border-border cursor-pointer transition-colors hover:bg-muted/50 ${!email.is_read && tab === 'inbox' ? 'bg-primary/5' : ''}`}
                    data-testid={`email-${email.email_id}`}>
                    <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
                      {!email.is_read && tab === 'inbox' ? <Mail className="w-4 h-4 text-primary" /> : <MailOpen className="w-4 h-4 text-muted-foreground" />}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <p className={`text-sm truncate ${!email.is_read && tab === 'inbox' ? 'font-bold text-foreground' : 'font-medium text-foreground'}`}>
                          {tab === 'inbox' ? email.from_name : email.to_name}
                        </p>
                        <span className="text-[10px] text-muted-foreground shrink-0">{formatDate(email.created_at)}</span>
                      </div>
                      <p className={`text-xs truncate ${!email.is_read && tab === 'inbox' ? 'text-foreground' : 'text-muted-foreground'}`}>{email.subject || '(No Subject)'}</p>
                      <p className="text-[10px] text-muted-foreground truncate">{email.body?.substring(0, 80)}</p>
                    </div>
                    <button onClick={e => handleTrash(email.email_id, e)} className="p-1.5 hover:bg-muted rounded opacity-0 group-hover:opacity-100">
                      <Trash2 className="w-3.5 h-3.5 text-muted-foreground" />
                    </button>
                  </div>
                ))
              )}
            </div>
          )}
        </div>
      </div>

      {/* Compose Dialog */}
      <Dialog open={showCompose} onOpenChange={setShowCompose}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle>{compose.reply_to ? 'Reply' : 'Compose Email'}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <Input value={compose.to} onChange={e => setCompose(p => ({ ...p, to: e.target.value }))} placeholder="To (email address)" data-testid="compose-to" />
            <Input value={compose.subject} onChange={e => setCompose(p => ({ ...p, subject: e.target.value }))} placeholder="Subject" data-testid="compose-subject" />
            <Textarea value={compose.body} onChange={e => setCompose(p => ({ ...p, body: e.target.value }))} placeholder="Write your message..." className="min-h-[200px]" data-testid="compose-body" />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCompose(false)}>Cancel</Button>
            <Button onClick={handleCompose} data-testid="send-email-btn"><Send className="w-4 h-4 mr-1" /> Send</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
